import { execute } from '@/graphql/execute';
import type { BulkDeletionKind } from '@/graphql/graphql';
import { ANALYSIS_RUN_FILES_KEY } from '@/lib/analyses/runFilesKey';
import { analysisRunsQueryKey } from '@/lib/analyses/useAnalysisRuns';
import {
  bulkDeleteBatchesMutation,
  bulkDeleteLayersMutation,
  previewBulkDeletionQuery,
} from '@/queries/bulkDeletion.query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';

export type BulkDeleteKind = 'LAYERS' | 'BATCHES';

export function BulkDeleteDialog({
  projectId,
  kind,
  selectedIds,
  open,
  onOpenChange,
  onComplete,
  selectedSummary,
}: {
  projectId: string;
  kind: BulkDeleteKind;
  selectedIds: string[];
  open: boolean;
  onOpenChange(open: boolean): void;
  onComplete(): void;
  /** Replaces the "Selected items: N" line when the caller's selection is
   *  not counted in ids (the Analyses hub counts analyses; a comparison is
   *  two batches). */
  selectedSummary?: string;
}) {
  const { t } = useTranslation('project');
  const queryClient = useQueryClient();
  const ids = [...new Set(selectedIds)].sort();
  const selectionKey = ids.join(',');
  const preview = useQuery({
    queryKey: ['bulkDeletionPreview', projectId, kind, selectionKey],
    queryFn: () => execute(previewBulkDeletionQuery, { projectId, kind: kind as BulkDeletionKind, ids }),
    enabled: open && ids.length > 0,
    retry: false,
  });
  const deletion = useMutation({
    mutationFn: async () => {
      const fingerprint = preview.data?.previewBulkDeletion.fingerprint;
      if (!fingerprint) throw new Error(t('bulkDelete.previewUnavailable'));
      if (kind === 'LAYERS') {
        return execute(bulkDeleteLayersMutation, { projectId, ids, fingerprint });
      }
      return execute(bulkDeleteBatchesMutation, { projectId, ids, fingerprint });
    },
    onSuccess: () => {
      onComplete();
      onOpenChange(false);
      void Promise.all([
        queryClient.invalidateQueries({ queryKey: ['layers', projectId] }),
        queryClient.invalidateQueries({ queryKey: ['batches'] }),
        queryClient.invalidateQueries({ queryKey: ['deletionOperations', projectId] }),
        queryClient.invalidateQueries({ queryKey: analysisRunsQueryKey(projectId) }),
        // A deleted run / report batch takes its files off the Analyses page.
        queryClient.invalidateQueries({ queryKey: [ANALYSIS_RUN_FILES_KEY] }),
      ]);
    },
    onError: () => preview.refetch(),
  });
  const impact = preview.data?.previewBulkDeletion;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t(kind === 'LAYERS' ? 'bulkDelete.layerTitle' : 'bulkDelete.batchTitle')}</DialogTitle>
          <DialogDescription>{t('bulkDelete.irreversible')}</DialogDescription>
        </DialogHeader>
        {preview.isPending && <p role='status'>{t('bulkDelete.loadingPreview')}</p>}
        {preview.error && (
          <p role='alert' className='text-destructive'>
            {preview.error.message}
          </p>
        )}
        {impact && (
          <div className='space-y-2 text-sm'>
            <p data-testid='bulk-delete-selected'>
              {selectedSummary ?? t('bulkDelete.selectedCount', { count: impact.requestedCount })}
            </p>
            <ul className='list-disc pl-5'>
              <li>{t('bulkDelete.layerCount', { count: impact.layerCount })}</li>
              <li>{t('bulkDelete.uploadBatchCount', { count: impact.uploadBatchCount })}</li>
              <li>{t('bulkDelete.analysisBatchCount', { count: impact.analysisBatchCount })}</li>
              <li>{t('bulkDelete.attachmentCount', { count: impact.attachmentCount })}</li>
              <li>{t('bulkDelete.byproductCount', { count: impact.byproductCount })}</li>
            </ul>
            {kind === 'LAYERS' && <p>{t('bulkDelete.layerSourceRetained')}</p>}
            {kind === 'BATCHES' && <p>{t('bulkDelete.batchSourceDeleted')}</p>}
            <p>{t(kind === 'LAYERS' ? 'bulkDelete.groupDescendantsIncluded' : 'bulkDelete.batchContentsIncluded')}</p>
            {!!impact.sampleNames.length && <p>{impact.sampleNames.join(', ')}</p>}
          </div>
        )}
        {deletion.error && (
          <p role='alert' className='text-destructive'>
            {deletion.error.message.includes('Deletion selection changed') || deletion.error.message.includes('Batch is processing')
              ? t('bulkDelete.selectionChanged')
              : t('bulkDelete.deleteFailed')}
          </p>
        )}
        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            {t('bulkDelete.cancel')}
          </Button>
          <Button
            variant='destructive'
            disabled={!impact || preview.isFetching || deletion.isPending}
            onClick={() => deletion.mutate()}
          >
            {deletion.isPending ? t('bulkDelete.deleting') : t('bulkDelete.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
